import { Button } from "@mui/material";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { type AnalysisVersionInfo, AnalysisVersionInfoState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { DialogErrorText } from "./DialogErrorText";
import { useCloseAndDuplicate } from "./useCloseAndDuplicate";
import type { SourceVersion } from "./utils";

type Props = {
  readonly sourceVersion: SourceVersion | null;
  readonly latestVersion: AnalysisVersionInfo;
  readonly onClose: () => void;
};

export const CloseAndDuplicateDialog = ({ sourceVersion, latestVersion, onClose }: Props) => {
  const { t } = useResource();
  const { closeAndDuplicate, isPending, errorText, reset } = useCloseAndDuplicate();

  const textParams = {
    version: sourceVersion?.version,
    newVersion: latestVersion.version + 1,
    latestVersion: latestVersion.version,
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogModal
      size="sm"
      open={sourceVersion !== null}
      onClose={handleClose}
      isPending={isPending}
      closeLabel={t(Resources.Common.Cancel)}
      title={t(Resources.AnalysisDetail.Duplicate.ClosingLatest.Title, textParams)}
      subtitle={
        // after a failed copy the latest version is already closed, so the dialog stops announcing it
        latestVersion.state === AnalysisVersionInfoState.in_progress
          ? t(Resources.AnalysisDetail.Duplicate.ClosingLatest.TextClosingLatest, textParams)
          : t(Resources.AnalysisDetail.Duplicate.ClosingLatest.Text, textParams)
      }
      content={errorText && <DialogErrorText text={errorText} />}
      primaryAction={
        <Button
          variant="contained"
          loading={isPending}
          onClick={() => {
            if (sourceVersion) closeAndDuplicate({ sourceVersion, latestVersion }, { onSuccess: handleClose });
          }}
        >
          {t(Resources.AnalysisDetail.Duplicate.ClosingLatest.Confirm)}
        </Button>
      }
    />
  );
};
